import { useParams } from "react-router";
import { api } from "@/lib/api-client";
import {
  Chip,
  Button,
  Tabs,
  Tab,
  Breadcrumbs,
  BreadcrumbItem,
  addToast,
  ScrollShadow,
  useDisclosure,
  Skeleton,
} from "@heroui/react";
import { LOCALE_LABELS } from "../../../../../../shared/constants/locales";
import dayjs from "dayjs";
import { ArrowLeft, Copy, Mail, Phone, UserPen, Trash2 } from "lucide-react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { CustomerSubscriptions } from "./components/Subscriptions/CustomerSubscriptions";
import { LimeLabelSelector } from "@/Components/LimeLabelSelector";
import CustomerAppointments from "./components/CustomerAppointments/CustomerAppointments";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mantine/hooks";
import { ManageCustomerModal } from "@/Components/NextBase/ManageCustomerModal";
import { AlertButton } from "@/Components/NextBase/AlertButton";
import { deleteCustomerWithId } from "@/apiSchedule";
import { CustomerInfoCard } from "./components/CustomerInfoCard";
import { GetCustomer } from "@/server-types";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";

export const CustomerPage = ({
  customerId: propCustomerId,
}: {
  customerId?: number;
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const customerIdParam = Number(params.customerId);
  const customerId = propCustomerId ?? customerIdParam;

  const {
    data: customer,
    refetch: refetchCustomer,
    processedErrorMessage: customerErrorMessage,
  } = api.customer.useGetCustomerNew(customerId);

  const {
    data: labels,
    refetch: refetchLabels,
    isFetching: isFetchingLabels,
    processedErrorMessage: labelsErrorMessage,
  } = api.customer.useGetCustomerLabels();

  const { mutateAsync: updateCustomer } = api.customer.usePutCustomer();

  const { mutateAsync: createLabel } = api.customer.usePostCustomerLabel();

  const {
    isOpen: isEditDrawerOpen,
    onOpen: onOpenEditDrawer,
    onClose: onCloseEditDrawer,
  } = useDisclosure();

  const handleLabelsChange = async (labelIds: number[]) => {
    await updateCustomer({
      customerId: customerId,
      body: {
        labels: labelIds,
      },
    });
    refetchCustomer();
  };

  const handleCreateLabel = async (name: string) => {
    const newLabel = await createLabel({
      name,
    });
    refetchLabels();
    return newLabel.id;
  };

  if (!customer) {
    return null; // TODO: loading state
  }

  return (
    <>
      <LimeAlert message={customerErrorMessage} className="m-2" />

      <div className="flex flex-col gap-0 rounded-2xl bg-white p-2 sm:gap-8 md:pl-4">
        <div className="flex flex-col gap-1 pl-4 md:pl-0">
          <div className="flex flex-col items-start">
            {!propCustomerId ? (
              <Breadcrumbs>
                <BreadcrumbItem>
                  <Button
                    variant="light"
                    onPress={() => navigate("/dashboard/customers")}
                    size="sm"
                    startContent={<ArrowLeft size={16} />}
                  >
                    <Trans>Stranke</Trans>
                  </Button>
                </BreadcrumbItem>
              </Breadcrumbs>
            ) : null}

            <div className="flex w-full flex-row items-center justify-between">
              <h2 className="text-2xl font-bold">
                {customer.name} {customer.lastName}
              </h2>
              <div className="flex gap-2">
                <Button
                  startContent={<UserPen className="h-4 w-4" />}
                  size="sm"
                  onPress={onOpenEditDrawer}
                >
                  <Trans>Uredi</Trans>
                </Button>
                <AlertButton
                  buttonProps={{
                    isIconOnly: true,
                    color: "danger",
                    size: "sm",
                  }}
                  confirmProps={{
                    onClick: async () => {
                      await deleteCustomerWithId(customerId);

                      addToast({
                        title: t`Stranka je bila izbrisana`,
                        color: "success",
                      });
                    },
                  }}
                  title={t`Izbriši stranko`}
                  content={t`Ali ste prepričani, da želite izbrisati stranko ${customer.name} ${customer.lastName}? Te akcije ni mogoče razveljaviti.`}
                >
                  <Trash2 className="h-4 w-4" />
                </AlertButton>
              </div>
            </div>
          </div>
          <ScrollShadow className="flex flex-row gap-2">
            {isFetchingLabels ? (
              <Skeleton className="h-8 w-44 rounded-sm" />
            ) : (
              customer.customerLabels.map((customerLabel) => (
                <Chip
                  key={customerLabel.customerLabel.id}
                  size="sm"
                  radius="sm"
                  className="group h-8"
                  variant="flat"
                >
                  {customerLabel.customerLabel.name}
                </Chip>
              ))
            )}
            <LimeLabelSelector
              isDisabled={isFetchingLabels || labelsErrorMessage != null}
              allLabels={labels || []}
              selectedLabelIds={customer.customerLabels.map(
                (customerLabel) => customerLabel.customerLabel.id,
              )}
              onLabelsChange={handleLabelsChange}
              onCreateLabel={handleCreateLabel}
            />
          </ScrollShadow>
        </div>

        <InfoRow customer={customer} />
        <div className="flex flex-col">
          <Tabs
            variant="light"
            classNames={{
              tab: "rounded-b-none",
              tabList: "p-0 rounded-b-none",
              cursor: "bg-zinc-50 shadow-none rounded-b-none",
              panel: "bg-zinc-50 rounded-lg p-4",
            }}
          >
            <Tab key="appointments" title={<Trans>Termini</Trans>}>
              <CustomerAppointments
                customerAppointments={customer.customerAppointments}
              />
            </Tab>
            <Tab key="subscriptions" title={<Trans>Naročnine</Trans>}>
              <CustomerSubscriptions customerId={Number(customerId)} />
            </Tab>
            <Tab key="calendar" title={<Trans>Koledar</Trans>}>
              <div>
                <h1>Koledar</h1>
              </div>
            </Tab>
            <Tab key="analytics" title={<Trans>Analitika</Trans>}>
              <div>
                <h1>Analitika</h1>
              </div>
            </Tab>
            <Tab key="files" title={<Trans>Datoteke</Trans>}>
              <div>
                <h1>Datoteke</h1>
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* {isMobile ? (
          <Tabs variant="underlined">
            <Tab key="appointments" title={<Trans>Termini</Trans>}>
              <CustomerAppointments
                customerAppointments={customer.customerAppointments}
              />
            </Tab>
            <Tab key="subscriptions" title={<Trans>Naročnine</Trans>}>
              <CustomerSubscriptions customerId={Number(customerId)} />
            </Tab>
            <Tab key="calendar" title={<Trans>Koledar</Trans>}>
              <div className="w-full rounded-lg border-1 border-gray-200 bg-white p-4">
                <AppointmentCalendar
                  appointments={customer.customerAppointments}
                />
              </div>
            </Tab>
            <Tab key="analytics" title={<Trans>Analitika</Trans>}>
              <Analytics customer={customer} />
            </Tab>
            <Tab key="files" title={<Trans>Datoteke</Trans>}>
              <CustomerFiles customerId={Number(customerId)} />
            </Tab>
          </Tabs>
        ) : (
          <>
            <div className="flex gap-2">
              <div className="w-full flex-1 rounded-lg border-1 border-gray-200 bg-white p-4">
                <Tabs variant="underlined" size="lg">
                  <Tab key="appointments" title={<Trans>Termini</Trans>}>
                    <CustomerAppointments
                      customerAppointments={customer.customerAppointments}
                    />
                  </Tab>
                  <Tab key="subscriptions" title={<Trans>Naročnine</Trans>}>
                    <CustomerSubscriptions customerId={Number(customerId)} />
                  </Tab>
                </Tabs>
              </div>
              <div className="w-full rounded-lg border-1 border-gray-200 bg-white p-4 md:w-auto">
                <AppointmentCalendar
                  appointments={customer.customerAppointments}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-full flex-1 rounded-lg border-1 border-gray-200 bg-white p-4">
                <h2 className="mb-2 text-xl font-medium">
                  <Trans>Analitika</Trans>
                </h2>
                <Analytics customer={customer} />
              </div>
              <div className="w-2/5 rounded-lg border-1 border-gray-200 bg-white p-4">
                <h2 className="mb-2 text-xl font-medium">
                  <Trans>Datoteke</Trans>
                </h2>
                <CustomerFiles customerId={Number(customerId)} />
              </div>
            </div>
          </>
        )} */}
      </div>

      <ManageCustomerModal
        //variant="drawer"
        isOpen={isEditDrawerOpen}
        handleClose={() => {
          onCloseEditDrawer();
          refetchCustomer();
        }}
        editingEntityId={Number(customerId)}
        defaultEntityType="physical"
      />
    </>
  );
};

const InfoRow = ({ customer }: { customer: GetCustomer["response"] }) => {
  return (
    <div className="hidden w-full flex-row gap-2 md:flex">
      <CustomerInfoCard
        label={t`Email`}
        value={customer.email || t`Email ni podan`}
        icon={Mail}
        href={customer.email ? `mailto:${customer.email}` : undefined}
      />
      <CustomerInfoCard
        label={t`Telefon`}
        value={
          customer.countryCode && customer.gsm
            ? `+${customer.countryCode}${customer.gsm}`
            : t`Telefonska številka ni podana`
        }
        icon={Phone}
        href={
          customer.gsm
            ? `tel:+${customer.countryCode}${customer.gsm}`
            : undefined
        }
      />
      <CustomerInfoCard
        label={t`Datum rojstva`}
        value={
          customer.birthday
            ? dayjs(customer.birthday).format("DD.MM.YYYY")
            : "-"
        }
      />
      <CustomerInfoCard
        label={t`Jezik`}
        value={LOCALE_LABELS[customer.language]}
      />
      <CustomerInfoCard
        label={t`Link za prenaročanje`}
        value={t`Link za spletno formo`}
        icon={Copy}
        onClick={() => {
          navigator.clipboard.writeText(
            `${import.meta.env.VITE_FORM_URL}/customer/${customer.customerHash}`,
          );
          addToast({
            color: "success",
            title: t`Vrednost je bila kopirana!`,
          });
        }}
      />
    </div>
  );
};
